import { scrollParentOf, scrollportBounds } from "@/utils/scroll-utils";

export const GRID_COLS = 12;

/**
 * Smallest footprint a widget may resize to, in cells: enough columns to stay legible and 2 rows for
 * axis + plot. Per-placement `minW`/`minH` override these.
 */
export const DEFAULT_MIN_W = 3;
export const DEFAULT_MIN_H = 2;

/** Edge band that triggers drag auto-scroll, and the speed reached at the edge itself. */
export const AUTO_SCROLL_EDGE_PX = 80;
export const AUTO_SCROLL_MAX_PX_PER_SEC = 1200;

/** Cell height in px. A default h=4 widget minus the chart's 64px chrome reproduces the old static
 * grid's 280px plot, so existing dashboards look unchanged without a data migration. */
export const DEFAULT_ROW_HEIGHT_PX = 89;

/** Gutter between widgets, split half to each side of a cell. */
export const GRID_GAP_PX = 12;

/** Signed px/sec for a pointer at `clientY`, ramping to the max at each edge and holding there
 * beyond it, so a pointer dragged off-screen scrolls at a bounded speed. */
export const autoScrollVelocity = (
  clientY: number,
  bounds: { top: number; bottom: number },
): number => {
  const ramp = (depth: number) =>
    (Math.min(depth, AUTO_SCROLL_EDGE_PX) / AUTO_SCROLL_EDGE_PX) * AUTO_SCROLL_MAX_PX_PER_SEC;
  const intoTop = bounds.top + AUTO_SCROLL_EDGE_PX - clientY;
  if (intoTop > 0) return -ramp(intoTop);
  const intoBottom = clientY - (bounds.bottom - AUTO_SCROLL_EDGE_PX);
  if (intoBottom > 0) return ramp(intoBottom);
  return 0;
};

export interface WidgetLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * One placed widget. Presentation-free: the grid positions it and calls the host's `renderBody`, so
 * the same engine can serve charts today and other widget kinds later.
 */
export interface WidgetPlacement {
  id: string;
  layout: WidgetLayout;
  /** Optional per-placement minimum footprint; falls back to DEFAULT_MIN_W/H. */
  minW?: number;
  minH?: number;
}

export const layoutsEqual = (a: WidgetLayout, b: WidgetLayout): boolean =>
  a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

/** Axis-aligned overlap test in grid-cell units. */
export const rectsOverlap = (a: WidgetLayout, b: WidgetLayout): boolean =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

/**
 * True when two placements share a grid edge (overlap on the other axis; corner-only doesn't count).
 * Decides whether a drop swaps with a neighbor or just inserts.
 */
export const areNeighbors = (a: WidgetLayout, b: WidgetLayout): boolean => {
  const xOverlap = a.x < b.x + b.w && b.x < a.x + a.w;
  const yOverlap = a.y < b.y + b.h && b.y < a.y + a.h;
  const touchHorizontally = (a.x + a.w === b.x || b.x + b.w === a.x) && yOverlap;
  const touchVertically = (a.y + a.h === b.y || b.y + b.h === a.y) && xOverlap;
  return touchHorizontally || touchVertically;
};

/** Snap to integer cells and keep the placement inside the grid horizontally. */
export const clampLayout = (layout: WidgetLayout, cols: number = GRID_COLS): WidgetLayout => {
  const w = Math.max(1, Math.min(cols, Math.round(layout.w)));
  const h = Math.max(1, Math.round(layout.h));
  const x = Math.max(0, Math.min(cols - w, Math.round(layout.x)));
  const y = Math.max(0, Math.round(layout.y));
  return { x, y, w, h };
};

/** How far down `findFreeSpotInBand` searches an unbounded band before giving up. */
const MAX_SEARCH_ROWS = 1000;

/** First top-left cell where a w×h placement fits without overlapping existing ones, searching only
 * rows fully inside `[startY, endY)`. Null when the band has no room; `Infinity` searches open-ended. */
export const findFreeSpotInBand = (
  layouts: readonly WidgetLayout[],
  w: number,
  h: number,
  startY: number,
  endY: number,
  cols: number = GRID_COLS,
): { x: number; y: number } | null => {
  const lastY = Math.min(endY - h, startY + MAX_SEARCH_ROWS);
  for (let y = startY; y <= lastY; y++) {
    for (let x = 0; x <= cols - w; x++) {
      const cand: WidgetLayout = { x, y, w, h };
      if (!layouts.some((l) => rectsOverlap(cand, l))) {
        return { x, y };
      }
    }
  }
  return null;
};

/** First top-left cell where a w×h placement fits without overlapping existing ones. */
export const findFreeSpot = (
  layouts: readonly WidgetLayout[],
  w: number,
  h: number,
  cols: number = GRID_COLS,
): { x: number; y: number } =>
  findFreeSpotInBand(layouts, w, h, 0, Infinity, cols) ?? { x: 0, y: 0 };

/**
 * Float every placement up into empty space above it (Datadog-style vertical fill), settling top-to-
 * bottom. `fixedId` pins one placement as an obstacle (the widget under the cursor); omit to settle all.
 */
export const compactVertical = (
  placements: readonly WidgetPlacement[],
  fixedId?: string,
  priorityId?: string,
): WidgetPlacement[] => {
  const result = structuredClone(placements) as WidgetPlacement[];
  const placed: WidgetPlacement[] = [];
  const fixed = fixedId != null ? result.find((w) => w.id === fixedId) : undefined;
  if (fixed != null) placed.push(fixed);
  // `priorityId` (the just-dragged placement) wins row ties, so dragging a smaller widget onto a larger
  // one claims the slot instead of losing to the incumbent.
  const sortY = (w: WidgetPlacement) => (w.id === priorityId ? w.layout.y - 0.5 : w.layout.y);
  const movers = result
    .filter((w) => w.id !== fixedId)
    .sort((a, b) => sortY(a) - sortY(b) || a.layout.x - b.layout.x);
  for (const mover of movers) {
    let y = mover.layout.y;
    // Push down out of any overlap at this row (separate same-row collisions instead of stacking) ...
    while (placed.some((p) => rectsOverlap({ ...mover.layout, y }, p.layout))) y += 1;
    // ... then float up into whatever free space is above.
    while (y > 0 && !placed.some((p) => rectsOverlap({ ...mover.layout, y: y - 1 }, p.layout))) {
      y -= 1;
    }
    mover.layout.y = y;
    placed.push(mover);
  }
  return result;
};

/**
 * Plan the chain shift that pushes `neighbor` in `dir` until it no longer overlaps `active`,
 * pushing widgets in its way along. Returns id → new x, or null when any widget would leave [0, cols).
 */
const planHorizontalPush = (
  items: readonly WidgetPlacement[],
  active: WidgetPlacement,
  neighbor: WidgetPlacement,
  dir: 1 | -1,
  cols: number,
): Map<string, number> | null => {
  const shifts = new Map<string, number>();
  const shiftedX = (w: WidgetPlacement) => shifts.get(w.id) ?? w.layout.x;
  // Push `pushed` flush against `pusher`'s far edge in `dir`; false when that exits the grid.
  const push = (pushed: WidgetPlacement, pusher: WidgetLayout): boolean => {
    const x = dir === 1 ? pusher.x + pusher.w : pusher.x - pushed.layout.w;
    if (x < 0 || x + pushed.layout.w > cols) return false;
    // Only ever push further in `dir`, so cascades are monotone and terminate.
    if (dir === 1 ? x <= shiftedX(pushed) : x >= shiftedX(pushed)) return true;
    shifts.set(pushed.id, x);
    return true;
  };
  if (!push(neighbor, active.layout)) return null;
  // Settle ripples: any widget overlapping a shifted widget gets pushed past it, until a fixpoint.
  for (let changed = true; changed; ) {
    changed = false;
    for (const [movedId] of shifts) {
      const moved = items.find((w) => w.id === movedId);
      if (moved == null) continue;
      const movedRect = { ...moved.layout, x: shiftedX(moved) };
      for (const other of items) {
        if (other.id === movedId || other.id === active.id) continue;
        const otherRect = { ...other.layout, x: shiftedX(other) };
        if (!rectsOverlap(movedRect, otherRect)) continue;
        const before = shiftedX(other);
        if (!push(other, movedRect)) return null;
        if (shiftedX(other) !== before) changed = true;
      }
    }
  }
  return shifts;
};

/**
 * Slide neighbors overlapped by the active placement toward their row's free space (away from the
 * active widget's center), pushing the row's other widgets along. Neighbors whose row has no room are
 * left overlapping for vertical compaction to resolve.
 */
export const resolveOverlapsHorizontally = (
  placements: readonly WidgetPlacement[],
  activeId: string,
  cols: number = GRID_COLS,
): WidgetPlacement[] => {
  const items = structuredClone(placements) as WidgetPlacement[];
  const active = items.find((w) => w.id === activeId);
  if (active == null) return items;
  for (const neighbor of items) {
    if (neighbor.id === activeId || !rectsOverlap(neighbor.layout, active.layout)) continue;
    // Only side collisions (narrow-in-x, deep-in-y overlap) slide. A wide, shallow overlap means
    // the active widget came from above/below, and sliding would garble the neighbor's column.
    const a = active.layout;
    const n = neighbor.layout;
    const overlapW = Math.min(a.x + a.w, n.x + n.w) - Math.max(a.x, n.x);
    const overlapH = Math.min(a.y + a.h, n.y + n.h) - Math.max(a.y, n.y);
    if (overlapW > overlapH) continue;
    const dir: 1 | -1 =
      neighbor.layout.x + neighbor.layout.w / 2 >= active.layout.x + active.layout.w / 2 ? 1 : -1;
    const shifts = planHorizontalPush(items, active, neighbor, dir, cols);
    if (shifts == null) continue;
    for (const [id, x] of shifts) {
      const moved = items.find((w) => w.id === id);
      if (moved != null) moved.layout.x = x;
    }
  }
  return items;
};

/**
 * Set placement `id` to `layout` and settle the grid: overlapped side neighbors slide toward free row
 * space first (FE-914); whatever cannot fit horizontally is pushed down by vertical compaction.
 */
export const applyLayout = (
  placements: readonly WidgetPlacement[],
  id: string,
  layout: WidgetLayout,
  cols: number = GRID_COLS,
): WidgetPlacement[] =>
  compactVertical(
    resolveOverlapsHorizontally(
      placements.map((w) => (w.id === id ? { ...w, layout: { ...layout } } : w)),
      id,
      cols,
    ),
    undefined,
    id,
  );

/**
 * Place placement `id` at `target` and re-settle. Landing on an edge-neighbor's top-left cell swaps
 * the two; a wider placement moving right also swaps once it overlaps its narrower neighbor,
 * because grid clamping can keep its top-left from reaching that slot. Other landings use
 * {@link applyLayout}.
 */
export const applyMove = (
  placements: readonly WidgetPlacement[],
  id: string,
  target: WidgetLayout,
  origin: WidgetLayout,
  cols: number = GRID_COLS,
): WidgetPlacement[] => {
  const occupant = placements.find((w) => {
    if (w.id === id) return false;
    if (w.layout.x === target.x && w.layout.y === target.y) return true;

    // A wider left placement exchanges order when its footprint enters the narrower right slot;
    // requiring matching top-left cells can make the exchange unreachable near the grid edge.
    return (
      origin.x + origin.w === w.layout.x &&
      origin.w > w.layout.w &&
      target.y === w.layout.y &&
      rectsOverlap(target, w.layout)
    );
  });
  if (occupant != null && areNeighbors(origin, occupant.layout)) {
    const movedLayout = structuredClone(target);
    const occupantLayout = structuredClone(occupant.layout);
    occupantLayout.x = origin.x;
    occupantLayout.y = origin.y;
    const horizontallyAdjacent =
      origin.x + origin.w === occupant.layout.x ||
      occupant.layout.x + occupant.layout.w === origin.x;
    const pairLeft = Math.min(origin.x, occupant.layout.x);
    if (horizontallyAdjacent) {
      // Exchanging raw x coordinates only works for equal widths. Repack the pair in its original
      // span so unequal neighbors truly exchange order without leaving a gap or crossing the grid.
      if (origin.x < occupant.layout.x) {
        occupantLayout.x = pairLeft;
        movedLayout.x = pairLeft + occupant.layout.w;
      } else {
        movedLayout.x = pairLeft;
        occupantLayout.x = pairLeft + target.w;
      }
    }
    const items = placements.map((w) => {
      if (w.id === id) return { ...w, layout: movedLayout };
      if (w.id === occupant.id) return { ...w, layout: occupantLayout };
      return w;
    });
    return compactVertical(items, undefined, id);
  }
  return applyLayout(placements, id, target, cols);
};

// ---------------------------------------------------------------------------
// Pixel mapping — cell space to the absolute rects the grid renders.
// ---------------------------------------------------------------------------

/** A rectangle in CSS pixels (left/top/width/height), as opposed to a cell-space `WidgetLayout`. */
export interface RectPx {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** One cell's pixel size. `colWidth` is measured; `rowHeight` is fixed per grid. */
export interface CellSizePx {
  colWidth: number;
  rowHeight: number;
}

/** Inset a cell-space outer rect to the visible widget rect: half the gutter per side. */
export const insetRectPx = (
  left: number,
  top: number,
  width: number,
  height: number,
  gap: number = GRID_GAP_PX,
): RectPx => ({
  left: left + gap / 2,
  top: top + gap / 2,
  width: width - gap,
  height: height - gap,
});

/** Snapped grid layout → pixel rect. */
export const cellRectPx = (l: WidgetLayout, cell: CellSizePx, gap: number = GRID_GAP_PX): RectPx =>
  insetRectPx(
    l.x * cell.colWidth,
    l.y * cell.rowHeight,
    l.w * cell.colWidth,
    l.h * cell.rowHeight,
    gap,
  );

/** What a gesture is doing, in the terms its geometry depends on. */
export interface GestureGeometryInput {
  type: "drag" | "resize";
  /** Which corner a resize grabbed; ignored for a drag. */
  corner?: "se" | "sw";
  minW: number;
  minH: number;
  /** Footprint when the gesture started, which every offset is measured from. */
  origLayout: WidgetLayout;
  dxPx: number;
  dyPx: number;
}

/** Where a gesture puts the widget: `layout` is the cell footprint to settle into, `rect` the pixel
 * rect tracking the pointer until it does. SW resizes anchor the right edge, SE the left. */
export const gestureGeometry = (
  { type, corner, minW, minH, origLayout: orig, dxPx, dyPx }: GestureGeometryInput,
  cell: CellSizePx,
  cols: number = GRID_COLS,
  gap: number = GRID_GAP_PX,
  maxRows?: number,
): { layout: WidgetLayout; rect: RectPx } => {
  const { colWidth, rowHeight } = cell;
  const leftPx = orig.x * colWidth;
  const topPx = orig.y * rowHeight;
  const maxResizeRows = maxRows == null ? Number.POSITIVE_INFINITY : maxRows - orig.y;
  const draggedHeightPx = Math.min(
    maxResizeRows * rowHeight,
    Math.max(minH * rowHeight, orig.h * rowHeight + dyPx),
  );
  const resizedH = Math.min(
    maxResizeRows,
    Math.max(minH, Math.round((orig.h * rowHeight + dyPx) / rowHeight)),
  );

  if (type === "drag") {
    const maxDragY = maxRows == null ? Number.POSITIVE_INFINITY : maxRows - orig.h;
    const draggedTopPx = Math.max(0, Math.min(maxDragY * rowHeight, topPx + dyPx));
    return {
      layout: clampLayout(
        {
          x: Math.round((leftPx + dxPx) / colWidth),
          y: Math.max(0, Math.min(maxDragY, Math.round((topPx + dyPx) / rowHeight))),
          w: orig.w,
          h: orig.h,
        },
        cols,
      ),
      rect: insetRectPx(leftPx + dxPx, draggedTopPx, orig.w * colWidth, orig.h * rowHeight, gap),
    };
  }

  if (corner === "sw") {
    const right = orig.x + orig.w;
    const rightEdgePx = right * colWidth;
    const targetX = Math.max(0, Math.min(right - minW, Math.round((leftPx + dxPx) / colWidth)));
    const draggedLeftPx = Math.max(0, Math.min(rightEdgePx - minW * colWidth, leftPx + dxPx));
    return {
      layout: clampLayout({ x: targetX, y: orig.y, w: right - targetX, h: resizedH }, cols),
      rect: insetRectPx(draggedLeftPx, topPx, rightEdgePx - draggedLeftPx, draggedHeightPx, gap),
    };
  }

  const draggedRightPx = Math.max(
    (orig.x + minW) * colWidth,
    Math.min(cols * colWidth, (orig.x + orig.w) * colWidth + dxPx),
  );
  return {
    layout: clampLayout(
      {
        x: orig.x,
        y: orig.y,
        // Capped at the columns left of the widget, so an over-wide target can't make `clampLayout`
        // re-home it to x=0 and slide the edge the pointer isn't holding.
        w: Math.min(
          cols - orig.x,
          Math.max(minW, Math.round((orig.w * colWidth + dxPx) / colWidth)),
        ),
        h: resizedH,
      },
      cols,
    ),
    rect: insetRectPx(leftPx, topPx, draggedRightPx - leftPx, draggedHeightPx, gap),
  };
};

/** Rows at the top and bottom of the visible pane, measured from any element flush with the grid's
 * top. New widgets are placed against these so they land where the user is looking. Assumes the
 * grid renders at DEFAULT_ROW_HEIGHT_PX; a grid overriding `rowHeight` must pass its own. */
export const visibleGridRows = (
  gridEl: HTMLElement | null,
  topOverlay?: HTMLElement | null,
  rowHeight: number = DEFAULT_ROW_HEIGHT_PX,
): { first: number; last: number } => {
  if (gridEl == null) return { first: 0, last: 0 };
  const { top, bottom } = scrollportBounds(scrollParentOf(gridEl), topOverlay);
  const gridTop = gridEl.getBoundingClientRect().top;
  return {
    first: Math.max(0, Math.floor((top - gridTop) / rowHeight)),
    last: Math.max(0, Math.ceil((bottom - gridTop) / rowHeight) - 1),
  };
};

/** Everything bounding how far a gesture may auto-scroll, in client px except where noted. */
export interface AutoScrollLimits {
  portTop: number;
  portBottom: number;
  gridTop: number;
  /** Bottom of the lowest widget, px below the grid's top. */
  contentBottomPx: number;
  rowHeight: number;
  /** A resizing widget's pinned top, px below the grid's top; null while dragging. */
  resizeAnchorPx: number | null;
}

/** A scroll step trimmed to what the board can use: down only until the lowest widget plus a row of
 * landing space is in view, and up during a resize only to the widget's pinned top. */
export const clampAutoScrollStep = (stepPx: number, limits: AutoScrollLimits): number => {
  if (stepPx > 0) {
    const room = limits.gridTop + limits.contentBottomPx + limits.rowHeight - limits.portBottom;
    return Math.min(stepPx, Math.max(0, room));
  }
  if (stepPx < 0 && limits.resizeAnchorPx != null) {
    const room = limits.portTop - (limits.gridTop + limits.resizeAnchorPx);
    return Math.max(stepPx, -Math.max(0, room));
  }
  return stepPx;
};
